import { useAuth } from '@clerk/clerk-react';
import { useCallback, useRef } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { USER_ENDPOINTS } from '../config/api';
import type { SubscriptionsResponse, YouTubeSubscription } from '../types/api.types';
import { extractApiErrorMessage } from '../utils/api';

export const userSubscriptionsQueryKey = ['user', 'subscriptions'] as const;

type UseUserSubscriptionsResult = Omit<UseQueryResult<SubscriptionsResponse, Error>, 'data'> & {
  data: SubscriptionsResponse | null;
  subscriptions: YouTubeSubscription[];
  forceRefreshSubscriptions: () => ReturnType<UseQueryResult<SubscriptionsResponse, Error>['refetch']>;
};

export const useUserSubscriptions = (): UseUserSubscriptionsResult => {
  const { getToken, isSignedIn, isLoaded } = useAuth();
  const forceRefreshRef = useRef(false);

  const queryResult = useQuery<SubscriptionsResponse, Error>({
    queryKey: userSubscriptionsQueryKey,
    enabled: Boolean(isLoaded && isSignedIn),
    queryFn: async () => {
      const token = await getToken();
      if (!token) {
        throw new Error('Authentication required. Please sign in.');
      }

      const shouldForceRefresh = forceRefreshRef.current;
      const queryParam = shouldForceRefresh ? '?forceRefresh=true' : '';

      const response = await fetch(`${USER_ENDPOINTS.SUBSCRIPTIONS}${queryParam}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 401) {
        throw new Error('Authentication required. Please sign in.');
      }

      if (response.status === 404) {
        if (shouldForceRefresh) {
          forceRefreshRef.current = false;
        }
        const nowIso = new Date().toISOString();
        return {
          subscriptions: [],
          fetchedAt: nowIso,
          cachedFrom: 'database',
        };
      }

      if (response.status === 424) {
        let message = 'Your Google authorization expired. Please reconnect your YouTube account.';
        try {
          const body = (await response.json()) as Partial<SubscriptionsResponse> & { message?: string };
          if (typeof body?.message === 'string' && body.message.trim().length > 0) {
            message = body.message;
          }
        } catch {
          // fall back to default message
        }
        const reauthError = new Error(message);
        (reauthError as Error & { code?: string }).code = 'reauthorize_required';
        reauthError.name = 'ReauthorizeRequiredError';
        throw reauthError;
      }

      if (!response.ok) {
        const message = await extractApiErrorMessage(response, 'Failed to load subscriptions.');
        throw new Error(message);
      }

      const data: SubscriptionsResponse = await response.json();
      if (shouldForceRefresh) {
        forceRefreshRef.current = false;
      }
      return data;
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const { refetch } = queryResult;

  const forceRefreshSubscriptions = useCallback(async () => {
    forceRefreshRef.current = true;
    return refetch({ throwOnError: true });
  }, [refetch]);

  return {
    ...queryResult,
    data: queryResult.data ?? null,
    subscriptions: queryResult.data?.subscriptions ?? [],
    forceRefreshSubscriptions,
  };
};
